import React, { useState, useRef, useEffect } from "react"
import { auth } from "../../firebase"
import {
  signInWithEmailAndPassword,
  sendEmailVerification,
  signOut,
} from "firebase/auth"
import * as yup from "yup"
import { useNavigate, useParams } from "react-router-dom"
import axios from "axios"
import GLOBAL from "../../config/variable"
import { useUserContext } from "../../context/usercontext"
import termsPDF from "../login/components/Terms.pdf"
import logo from "../../assets/agencia-MD-logo.png"

// PrimeReact
import { Formik, Form } from "formik"
import { Button } from "primereact/button"
import { FloatLabel } from "primereact/floatlabel"
import { Password } from "primereact/password"
import { Checkbox } from "primereact/checkbox"
import { InputText } from "primereact/inputtext"
import { Toast } from "primereact/toast"
import { Message } from "primereact/message"
import { Image } from "primereact/image"
import PhoneInput from "react-phone-number-input"

import ROUTES from "../../router/routesConstants"

const registerSchema = yup.object().shape({
  name_first: yup.string().required("El nombre es requerido"),
  name_last: yup.string().required("El apellido es requerido"),
  email: yup
    .string()
    .email("Correo inválido")
    .required("El correo es requerido"),
  id_number: yup.string().required("El número de identificación es requerido"),
  phone_number: yup.string().required("El teléfono es requerido"),
  password: yup
    .string()
    .min(6, "La contraseña debe tener al menos 6 caracteres")
    .required("La contraseña es requerida"),
  referrer_code: yup.string().optional(),
})

function Register() {
  const navigate = useNavigate()
  const { code } = useParams()
  const { user } = useUserContext()
  const [error, setError] = useState(null)
  const [loading, setLoading] = useState(false)
  const [termsAccepted, setTermsAccepted] = useState(false)
  const [referenceuser, setReferenceuser] = useState(null)
  const [isvalidcode, setIsvalidcode] = useState(false)
  const [isInvalidCode, setIsInvalidCode] = useState(false)
  const toast = useRef(null)

  const IS_LOCAL = window.location.host === "localhost:3000"
  const RECAPTCHA_KEY = "6LdkCTorAAAAAMaJO07bq47IHohte_Mg8J--ZLKN"

  useEffect(() => {
    let script = null

    if (user) {
      auth.signOut()
    }

    if (!IS_LOCAL) {
      script = document.createElement("script")
      script.src = `https://www.google.com/recaptcha/api.js?render=${RECAPTCHA_KEY}`
      script.async = true
      document.body.appendChild(script)
    }

    return () => {
      if (script) {
        document.body.removeChild(script)
      }

      const badge = document.querySelector(".grecaptcha-badge")
      if (badge && badge.parentNode) {
        badge.parentNode.removeChild(badge)
      }

      if (window.grecaptcha) {
        delete window.grecaptcha
      }
    }
  }, [user, IS_LOCAL])

  useEffect(() => {
    if (code) {
      verifyCode(code)
    }
  }, [code])

  const verifyCode = async (code) => {
    try {
      const response = await axios.get(
        `${GLOBAL.SERVER_URL}/users/referral/${code}`
      )
      if (response.data.success) {
        setReferenceuser(response.data.data)
        setIsvalidcode(true)
      } else {
        console.error("error", response.data.message)
        setIsInvalidCode(true)
      }
    } catch (error) {
      console.error("Error verifying code:", error.response?.data?.message)
      setIsInvalidCode(true)
    }
  }

  const handleRegister = async (values) => {
    try {
      setLoading(true)
      setError(null)

      // Get reCAPTCHA token
      if (!IS_LOCAL) {
        const reCAPTCHA = await window.grecaptcha.execute(RECAPTCHA_KEY, {
          action: "submit",
        })
        values.reCAPTCHA = reCAPTCHA
      }

      await axios.post(`${GLOBAL.SERVER_URL}/users`, values)

      const userCredential = await signInWithEmailAndPassword(
        auth,
        values.email,
        values.password
      )

      await sendEmailVerification(userCredential.user)

      window.dataLayer.push({
        event: "sign_up",
        user_id: userCredential.user.uid,
        user_email: values.email,
        user_phone: values.phone_number,
        id_number: values.id_number,
        referrer_code: values.referrer_code || null,
      })

      toast.current.show({
        severity: "success",
        summary: "Success",
        detail:
          "Usuario registrado correctamente. Por favor verifica tu correo electrónico.",
        position: "top-center",
        life: 5000,
      })
      await signOut(auth)
      navigate(ROUTES.LOGIN, { replace: true, state: { registered: true } })
    } catch (error) {
      window.dataLayer.push({
        event: "sign_up_error",
        error_message: error.response?.data?.message,
        user_email: values.email,
        user_phone: values.phone_number,
      })
      if (error.response?.data?.message) {
        toast.current.show({
          severity: "error",
          summary: "Error en registro",
          detail: error.response?.data?.message,
          position: "top-center",
          life: 5000,
        })
      } else {
        setError("Error al registrar el usuario. Por favor intenta nuevamente.")
      }
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="login-container">
      <Toast ref={toast} life={5000} position="top-center" />
      <div className="w-full relative flex justify-center items-center">
        <i
          className="pi pi-angle-left text-xl text-white cursor-pointer"
          style={{ position: "absolute", left: 0, top: "calc(50% - 10px)" }}
          onClick={() => navigate(ROUTES.LOGIN)}
        />
        <Image src={logo} alt="Logo" className="mx-auto" />
      </div>
      <hr />

      <div className="title-container-register mb-4">
        <h2 className="text-white text-left">Registro</h2>
      </div>

      {error && (
        <Message severity="error" text={error} className="w-full my-3" />
      )}

      <Formik
        initialValues={{
          name_first: "",
          name_last: "",
          email: "",
          id_number: "",
          password: "",
          phone_number: "",
          referrer_code: code || "",
        }}
        validationSchema={registerSchema}
        onSubmit={async (values) => {
          handleRegister(values)
        }}
      >
        {({
          handleChange,
          handleBlur,
          setFieldValue,
          values,
          errors,
          touched,
        }) => (
          <Form className="formLogin flex flex-column gap-6 mb-6">
            <div className="flex flex-column">
              <FloatLabel>
                <InputText
                  id="name_first"
                  name="name_first"
                  type="text"
                  value={values.name_first}
                  onChange={handleChange}
                  onBlur={handleBlur}
                />
                <label className="text-white" htmlFor="name_first">
                  Nombre
                </label>
              </FloatLabel>
              {touched.name_first && errors.name_first && (
                <small className="form-text-err">{errors.name_first}</small>
              )}
            </div>

            <div className="flex flex-column">
              <FloatLabel>
                <InputText
                  id="name_last"
                  name="name_last"
                  type="text"
                  value={values.name_last}
                  onChange={handleChange}
                  onBlur={handleBlur}
                />
                <label className="text-white" htmlFor="name_last">
                  Apellido(s)
                </label>
              </FloatLabel>
              {touched.name_last && errors.name_last && (
                <small className="form-text-err">{errors.name_last}</small>
              )}
            </div>

            <div className="flex flex-column">
              <FloatLabel>
                <InputText
                  id="email"
                  name="email"
                  type="text"
                  value={values.email}
                  onChange={handleChange}
                  onBlur={handleBlur}
                />
                <label className="text-white" htmlFor="email">
                  Correo Electrónico
                </label>
              </FloatLabel>
              {touched.email && errors.email && (
                <small className="form-text-err">{errors.email}</small>
              )}
            </div>

            <div className="flex flex-column">
              <FloatLabel>
                <InputText
                  id="id_number"
                  name="id_number"
                  type="text"
                  value={values.id_number}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  maxLength={32}
                />
                <label className="text-white" htmlFor="id_number">
                  Número de identificación
                </label>
              </FloatLabel>
              {touched.id_number && errors.id_number && (
                <small className="form-text-err">{errors.id_number}</small>
              )}
            </div>

            <div className="flex flex-column">
              <FloatLabel>
                <PhoneInput
                  countryCallingCodeEditable={false}
                  defaultCountry="CR"
                  internationalMode="international"
                  layout="second"
                  value={values.phone_number}
                  onChange={(value) => setFieldValue("phone_number", value)}
                  onBlur={handleBlur}
                  error={touched.phone_number && errors.phone_number}
                  className="profile-input-register"
                />
                {touched.phone_number && errors.phone_number && (
                  <small className="form-text-err">{errors.phone_number}</small>
                )}
              </FloatLabel>
            </div>

            <div className="flex flex-column">
              <FloatLabel>
                <Password
                  id="password"
                  name="password"
                  value={values.password}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  toggleMask
                  promptLabel="Elige una contraseña"
                />
                <label className="text-white" htmlFor="password">
                  Contraseña
                </label>
              </FloatLabel>
              {touched.password && errors.password && (
                <small className="form-text-err">{errors.password}</small>
              )}
            </div>

            <div className="flex flex-column">
              <FloatLabel>
                <InputText
                  id="referrer_code"
                  name="referrer_code"
                  type="text"
                  value={values.referrer_code}
                  onChange={handleChange}
                  onBlur={handleBlur}
                />
                <label className="text-white" htmlFor="referrer_code">
                  Código de Referencia (opcional)
                </label>
              </FloatLabel>
              {isvalidcode && referenceuser && (
                <p className="text-sm text-white">
                  El usuario que te refirió es: {referenceuser.name_first}
                </p>
              )}
              {isInvalidCode && (
                <p className="text-sm " style={{ color: "#ffa400" }}>
                  Código de referencia inválido
                </p>
              )}
            </div>

            <div className="flex gap-2">
              <Checkbox
                id="terms"
                name="terms"
                checked={termsAccepted}
                onChange={(e) => setTermsAccepted(e.checked)}
              />
              <label htmlFor="terms" className="text-white text-sm">
                Acepto los{" "}
                <a
                  href={termsPDF}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-500 cursor-pointer"
                >
                  Términos y Condiciones
                </a>
              </label>
            </div>

            <Button
              severity="primary"
              className="w-full"
              label={loading ? "Registrando..." : "Registrarse"}
              type="submit"
              disabled={loading || !termsAccepted}
            />
          </Form>
        )}
      </Formik>
    </div>
  )
}

export default Register
